import {
  Badge,
  createTheme,
  defaultVariantColorsResolver,
  parseThemeColor,
  rem,
  v8CssVariablesResolver,
  type VariantColorsResolver,
} from '@mantine/core'
import badgeClasses from './badges.module.css'

// LOGO COLOR: #703EFF

// Mantine v9 changed the `light` variant (https://mantine.dev/guides/8x-to-9x/
// #light-variant-color-changes). Two coordinated changes restore the v8 look:
//
// 1. `cssVariablesResolver` (re-exported below, applied on <MantineProvider>) —
//    the official migration helper. It maps the `--mantine-color-*-light*` CSS
//    variables back to v8's semi-transparent tints. This alone fixes `light`
//    variants that use a base color name.
//
// 2. `variantColorResolver` (here) — v9 ALSO changed the resolver so a
//    shade-indexed color (e.g. "green.7", "red.7") renders a SOLID hex
//    background with near-same-hue dark text — unreadable (TOW-2154), and not
//    covered by (1) because it bypasses the CSS variables. We route shaded
//    `light` colors back through the `-light` / `-light-color` variables so
//    they pick up the v8 values from (1).
const variantColorResolver: VariantColorsResolver = (input) => {
  const resolved = defaultVariantColorsResolver(input)

  if (input.variant === 'light') {
    const parsed = parseThemeColor({ color: input.color, theme: input.theme })
    if (parsed.isThemeColor && parsed.shade !== undefined) {
      return {
        ...resolved,
        background: `var(--mantine-color-${parsed.color}-light)`,
        hover: `var(--mantine-color-${parsed.color}-light-hover)`,
        color: `var(--mantine-color-${parsed.color}-light-color)`,
        border: `${rem(1)} solid transparent`,
      }
    }
  }

  return resolved
}

// Applied on <MantineProvider> (cssVariablesResolver is a provider prop, not a
// theme option). Restores v8 `light` variant CSS variable values — see (1) above.
export const cssVariablesResolver = v8CssVariablesResolver

export const theme = createTheme({
  defaultRadius: 'md',
  primaryColor: 'tower-light-blue',
  variantColorResolver,
  colors: {
    'tower-light-blue': [
      '#e7f7ff',
      '#d8eaf7',
      '#b2d2e9',
      '#89b9db',
      '#67a4cf',
      '#5196c9',
      '#4390c7',
      '#3684bb',
      '#276f9f',
      '#0d608e',
    ],
    dark: [
      '#C9CDD3', // 0 - lightest (text on dark backgrounds)
      '#A8ADB5', // 1
      '#858B95', // 2
      '#5A6270', // 3
      '#3A424F', // 4
      '#252D38', // 5
      '#1A222B', // 6
      '#111820', // 7
      '#01101B', // 8
      '#000A12', // 9 - darkest (background)
    ],
    // dark: [    // leaving this for backup for old dark theme colors
    //   '#C1C2C5',
    //   '#A6A7AB',
    //   '#909296',
    //   '#5c5f66',
    //   '#373A40',
    //   '#2C2E33',
    //   '#25262b',
    //   '#1A1B1E',
    //   '#141517',
    //   '#101113',
    // ],
  },
  components: {
    Badge: Badge.extend({
      classNames: badgeClasses,
    }),
    Select: {
      defaultProps: {
        checkIconPosition: 'right',
      },
    },
  },
})
